/**
 * EligibilityRuleEditor — Visual editor for workflow eligibility rules.
 *
 * Allows admins to configure which entity context a workflow operates on,
 * set the action button appearance (label, color, description), and build
 * rules using dropdowns for field, operator, and value.
 */

import React, { useState } from 'react';
import {
  Box, Typography, TextField, FormControl, InputLabel, Select, MenuItem,
  Button, IconButton, Paper, Chip, Collapse, Divider, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RuleIcon from '@mui/icons-material/Rule';

// Available context fields per entity type
const CONTEXT_FIELDS = {
  subscription: [
    { value: 'subscription', label: 'Subscription Exists', type: 'exists' },
    { value: 'subscription.status', label: 'Subscription Status', type: 'select', options: ['active', 'trialing', 'past_due', 'paused', 'canceled', 'expired', 'admin_granted', 'pending'] },
    { value: 'subscription.cancel_at_period_end', label: 'Cancel at Period End', type: 'boolean' },
    { value: 'subscription.started_at', label: 'Started At', type: 'date' },
    { value: 'subscription.current_period_end', label: 'Current Period End', type: 'date' },
    { value: 'subscription.trial_end', label: 'Trial End', type: 'date' },
    { value: 'subscription.canceled_at', label: 'Canceled At', type: 'date' },
    { value: 'subscription.provider_subscription_id', label: 'Has Stripe Subscription', type: 'exists' },
    { value: 'subscription.dunning_started_at', label: 'Dunning Started', type: 'date' },
    { value: 'subscription.dunning_attempt_count', label: 'Dunning Attempt Count', type: 'number' },
    { value: 'plan.price_cents', label: 'Plan Price (cents)', type: 'number' },
    { value: 'plan.planId', label: 'Plan ID', type: 'string' },
    { value: 'plan.interval_type', label: 'Billing Interval', type: 'select', options: ['month', 'year', 'one_time'] },
    { value: 'plan.name', label: 'Plan Name', type: 'string' },
    { value: 'product.productId', label: 'Product ID', type: 'string' },
    { value: 'product.name', label: 'Product Name', type: 'string' },
    { value: 'customer.email', label: 'Customer Email', type: 'string' },
    { value: 'customer.is_flagged', label: 'Customer Flagged', type: 'boolean' },
    { value: 'customer.flag_reason', label: 'Flag Reason', type: 'string' },
    { value: 'customer.created_at', label: 'Customer Created At', type: 'date' },
  ],
  customer: [
    { value: 'customer.email', label: 'Customer Email', type: 'string' },
    { value: 'customer.is_flagged', label: 'Customer Flagged', type: 'boolean' },
    { value: 'customer.flag_reason', label: 'Flag Reason', type: 'string' },
    { value: 'customer.created_at', label: 'Customer Created At', type: 'date' },
  ],
  payment: [
    { value: 'payment.status', label: 'Payment Status', type: 'select', options: ['succeeded', 'failed', 'pending', 'refunded'] },
    { value: 'payment.amount_cents', label: 'Payment Amount (cents)', type: 'number' },
    { value: 'payment.refund_amount_cents', label: 'Refund Amount (cents)', type: 'number' },
    { value: 'payment.provider_payment_id', label: 'Has Stripe Payment', type: 'exists' },
    { value: 'customer.email', label: 'Customer Email', type: 'string' },
    { value: 'customer.is_flagged', label: 'Customer Flagged', type: 'boolean' },
    { value: 'customer.flag_reason', label: 'Flag Reason', type: 'string' },
  ],
};

// Operators grouped by compatible field types
const OPERATORS = [
  { value: 'eq', label: '=', types: ['string', 'number', 'boolean', 'select', 'date'] },
  { value: 'neq', label: '!=', types: ['string', 'number', 'boolean', 'select', 'date'] },
  { value: 'gt', label: '>', types: ['number', 'date'] },
  { value: 'gte', label: '>=', types: ['number', 'date'] },
  { value: 'lt', label: '<', types: ['number', 'date'] },
  { value: 'lte', label: '<=', types: ['number', 'date'] },
  { value: 'in', label: 'in', types: ['string', 'select'] },
  { value: 'not_in', label: 'not in', types: ['string', 'select'] },
  { value: 'exists', label: 'exists', types: ['string', 'number', 'date', 'exists'] },
  { value: 'contains', label: 'contains', types: ['string'] },
  { value: 'starts_with', label: 'starts with', types: ['string'] },
  { value: 'is_empty', label: 'is empty', types: ['string'] },
  { value: 'regex', label: 'matches regex', types: ['string'] },
];

const BUTTON_COLORS = ['inherit', 'primary', 'secondary', 'success', 'warning', 'error'];

function getFieldDef(entityType, fieldValue) {
  return (CONTEXT_FIELDS[entityType] || []).find(f => f.value === fieldValue);
}

function getCompatibleOperators(fieldType) {
  return OPERATORS.filter(op => op.types.includes(fieldType || 'string'));
}

/**
 * Parse a rule value for display. Handles arrays (for 'in' operator), booleans, etc.
 */
function formatValue(value) {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (value === null || value === undefined) return '';
  return String(value);
}

/**
 * Parse user input into a typed value based on field type and operator.
 */
function parseValue(input, fieldDef, operator) {
  if (operator === 'exists' || operator === 'is_empty') {
    return input === 'true' || input === true;
  }
  if (operator === 'in' || operator === 'not_in') {
    return input.split(',').map(s => s.trim()).filter(Boolean);
  }
  if (fieldDef?.type === 'boolean') {
    return input === 'true' || input === true;
  }
  if (fieldDef?.type === 'number') {
    return Number(input) || 0;
  }
  return input;
}

// ─── Single Rule Row ────────────────────────────────────────────

function RuleRow({ rule, entityType, onChange, onDelete }) {
  const fieldDef = getFieldDef(entityType, rule.field);
  const fieldType = fieldDef?.type || 'string';
  const compatibleOps = getCompatibleOperators(fieldType);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      {/* Field selector */}
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <Select
          value={rule.field || ''}
          onChange={e => onChange({ ...rule, field: e.target.value, operator: 'eq', value: '' })}
          displayEmpty
          renderValue={v => {
            const fd = getFieldDef(entityType, v);
            return fd ? fd.label : <Typography color="text.secondary" variant="body2">Select field...</Typography>;
          }}
        >
          {(CONTEXT_FIELDS[entityType] || []).map(f => (
            <MenuItem key={f.value} value={f.value}>
              <Box>
                <Typography variant="body2">{f.label}</Typography>
                <Typography variant="caption" color="text.secondary">{f.value}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Operator selector */}
      <FormControl size="small" sx={{ minWidth: 110 }}>
        <Select
          value={rule.operator || ''}
          onChange={e => onChange({ ...rule, operator: e.target.value })}
        >
          {compatibleOps.map(op => (
            <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Value input — adapts based on field type and operator */}
      {(rule.operator === 'exists' || rule.operator === 'is_empty') ? (
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={rule.value === true || rule.value === 'true' ? 'true' : 'false'}
            onChange={e => onChange({ ...rule, value: e.target.value === 'true' })}
          >
            <MenuItem value="true">true</MenuItem>
            <MenuItem value="false">false</MenuItem>
          </Select>
        </FormControl>
      ) : fieldDef?.type === 'boolean' ? (
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={rule.value === true || rule.value === 'true' ? 'true' : 'false'}
            onChange={e => onChange({ ...rule, value: e.target.value === 'true' })}
          >
            <MenuItem value="true">true</MenuItem>
            <MenuItem value="false">false</MenuItem>
          </Select>
        </FormControl>
      ) : (rule.operator === 'in' || rule.operator === 'not_in') && fieldDef?.options ? (
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            multiple
            value={Array.isArray(rule.value) ? rule.value : []}
            onChange={e => onChange({ ...rule, value: e.target.value })}
            renderValue={selected => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                {selected.map(v => <Chip key={v} label={v} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />)}
              </Box>
            )}
          >
            {fieldDef.options.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : fieldDef?.type === 'select' && fieldDef?.options ? (
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={rule.value || ''}
            onChange={e => onChange({ ...rule, value: e.target.value })}
          >
            {fieldDef.options.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <TextField
          size="small"
          value={formatValue(rule.value)}
          onChange={e => onChange({ ...rule, value: parseValue(e.target.value, fieldDef, rule.operator) })}
          placeholder={rule.operator === 'in' ? 'val1, val2, val3' : 'Value'}
          type={fieldDef?.type === 'number' ? 'number' : 'text'}
          sx={{ minWidth: 140 }}
        />
      )}

      <Tooltip title="Remove rule">
        <IconButton size="small" onClick={onDelete} sx={{ color: 'error.main' }} aria-label="Remove rule">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// ─── Main Component ─────────────────────────────────────────────

export default function EligibilityRuleEditor({ eligibility, onChange }) {
  const [expanded, setExpanded] = useState(!!eligibility);

  const elig = eligibility || {};
  const entityType = elig.entity_type || 'subscription';
  const rules = elig.rules || [];

  function update(patch) {
    onChange({ ...elig, ...patch });
  }

  function updateRule(index, updatedRule) {
    const updated = [...rules];
    updated[index] = updatedRule;
    update({ rules: updated });
  }

  function addRule() {
    update({ rules: [...rules, { field: '', operator: 'eq', value: '' }] });
  }

  function deleteRule(index) {
    update({ rules: rules.filter((_, i) => i !== index) });
  }

  const hasRules = rules.length > 0;

  return (
    <Paper variant="outlined" sx={{ mx: 2, mt: 1, overflow: 'hidden' }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
          cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <RuleIcon fontSize="small" color={hasRules ? 'primary' : 'disabled'} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Eligibility Rules
        </Typography>
        {hasRules && (
          <Chip label={`${rules.length} rule${rules.length !== 1 ? 's' : ''}`} size="small" color="primary" variant="outlined" />
        )}
        {!hasRules && !expanded && (
          <Typography variant="caption" color="text.secondary">No rules configured — workflow available everywhere</Typography>
        )}
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          {/* Metadata fields */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Entity Type</InputLabel>
              <Select
                value={entityType}
                label="Entity Type"
                onChange={e => update({ entity_type: e.target.value, rules: [] })}
              >
                <MenuItem value="subscription">Subscription</MenuItem>
                <MenuItem value="customer">Customer</MenuItem>
                <MenuItem value="payment">Payment</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small" label="Button Label"
              value={elig.label || ''}
              onChange={e => update({ label: e.target.value })}
              sx={{ minWidth: 160 }}
            />

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Button Style</InputLabel>
              <Select
                value={elig.color || 'inherit'}
                label="Button Style"
                onChange={e => update({ color: e.target.value })}
                renderValue={v => (
                  <Button size="small" variant="outlined" color={v === 'inherit' ? 'inherit' : v}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0, minHeight: 24, pointerEvents: 'none' }}>
                    {elig.label || 'Action'}
                  </Button>
                )}
              >
                {BUTTON_COLORS.map(c => (
                  <MenuItem key={c} value={c}>
                    <Button size="small" variant="outlined" color={c === 'inherit' ? 'inherit' : c}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, minHeight: 28, pointerEvents: 'none', mr: 1 }}>
                      {elig.label || 'Action'}
                    </Button>
                    <Typography variant="caption" color="text.secondary">{c}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small" label="Description"
              value={elig.description || ''}
              onChange={e => update({ description: e.target.value })}
              sx={{ flex: 1, minWidth: 200 }}
            />
          </Box>

          {/* Rules */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            All rules must pass (AND logic). The workflow action button is shown when all rules are satisfied.
          </Typography>

          {rules.map((rule, i) => (
            <RuleRow
              key={i}
              rule={rule}
              entityType={entityType}
              onChange={updated => updateRule(i, updated)}
              onDelete={() => deleteRule(i)}
            />
          ))}

          <Button
            size="small" startIcon={<AddIcon />}
            onClick={addRule}
            sx={{ textTransform: 'none', mt: 0.5 }}
          >
            Add Rule
          </Button>
        </Box>
      </Collapse>
    </Paper>
  );
}
